import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';

import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { UnitCalculatorDialogData, UnitRow } from '../../interfaces/dialog-data.interface';

@Component({
  selector: 'app-unit-calculator-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatRadioModule,
    TranslatePipe,
  ],
  templateUrl: './unit-calculator-modal.component.html',
})
export class UnitCalculatorModalComponent implements OnInit {
  units: UnitRow[] = [];
  fromUnitId: string | null = null;
  amountText = '';
  saleFactor = 1; // unidades base por 1 unidad de venta actual

  constructor(
    private dialogRef: MatDialogRef<UnitCalculatorModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UnitCalculatorDialogData
  ) {}

  ngOnInit(): void {
    const material = this.data.item.material ?? {};
    const aums: any[] = material.alternalUnitOfMeasures ?? [];
    const saleLabel = this.data.item.unitLabel;

    // factor de la unidad de venta actual (desde la lista cruda, antes de filtrarla)
    const saleUnit = aums.find((a) => a?.unitOfMeasure?._id?.toString() === this.data.item.unitMeasureId);
    this.saleFactor = Number(saleUnit?.conversionFactor ?? saleUnit?.equivalentQuantity ?? 1);

    this.units = aums
      .filter((a) => a?.unitOfMeasure)
      // UMP oculta cuando la compañía no la tiene habilitada
      .filter((a) => this.data.umpActive !== false || a.isUMP !== true)
      // no tiene sentido convertir la misma unidad de venta contra sí misma
      .filter((a) => a.unitOfMeasure._id?.toString() !== this.data.item.unitMeasureId)
      .map((a) => {
        const factor = Number(a.conversionFactor ?? a.equivalentQuantity ?? 1);
        return {
          unitMeasureId: a.unitOfMeasure._id?.toString(),
          code: a.unitOfMeasure.code ?? a.unitOfMeasure.name,
          name: a.unitOfMeasure.name,
          factor,
          chip: a.isUMB ? 'BASE' : a.isSecondaryAlternativeUom ? 'SECUNDARIA' : a.isUMP ? 'PARALELA' : null,
          isUMP: a.isUMP === true,
          // equivalencia contra la unidad de venta actual (no la base)
          equivalence: `1 ${a.unitOfMeasure.name} = ${this.fmt(factor / (this.saleFactor || 1))} ${saleLabel}`,
          stock: 0,
          price: 0,
          isCommercialUom: a.isCommercialUom !== false,
        } as UnitRow;
      });

    // orden: Base, Secundaria, Paralela y luego el resto
    this.units.sort((a, b) => this.chipRank(a.chip) - this.chipRank(b.chip));
  }

  // formatea un factor con hasta 3 decimales sin ceros sobrantes
  private fmt(n: number): string {
    return Number.isFinite(n) ? String(parseFloat(n.toFixed(3))) : '0';
  }

  private chipRank(chip: UnitRow['chip']): number {
    switch (chip) {
      case 'BASE':
        return 0;
      case 'SECUNDARIA':
        return 1;
      case 'PARALELA':
        return 2;
      default:
        return 3;
    }
  }

  get saleLabel(): string {
    return this.data.item.unitLabel;
  }

  get fromUnit(): UnitRow | undefined {
    return this.units.find((u) => u.unitMeasureId === this.fromUnitId);
  }

  get amount(): number {
    const raw = (this.amountText ?? '').trim();
    return raw === '' || raw === '.' ? 0 : parseFloat(raw);
  }

  // cantidad convertida a la unidad de venta actual
  get converted(): number {
    const from = this.fromUnit;
    if (!from || !this.amount) return 0;
    return (this.amount * from.factor) / (this.saleFactor || 1);
  }

  onAmountInput(value: string): void {
    let raw = (value ?? '').replace(/[^0-9.]/g, '');
    const firstDot = raw.indexOf('.');
    if (firstDot !== -1) {
      raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
    }
    this.amountText = raw;
  }

  confirm(): void {
    if (!this.fromUnit || this.converted <= 0) return;
    this.dialogRef.close(this.converted);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
